// Bulk-add destination — the orchestrator for the whole "ajout en masse" flow.
// All session state lives in BulkAddProvider (mounted in the /words layout, so it
// survives navigation); this component just selects which screen to show:
//   - an open draft still in the input phase          → the input page (hydrated)
//   - an open session that is still analyzing / ready → the loading screen
//   - an open session being reviewed / errored        → the review screen
//   - otherwise the input page (creating new) or the entry screen.
import { BulkEntry } from '@/components/words/bulk-add/bulk-entry';
import { BulkInput } from '@/components/words/bulk-add/bulk-input';
import { BulkLoading } from '@/components/words/bulk-add/bulk-loading';
import { BulkReview } from '@/components/words/bulk-add/bulk-review';
import { useBulkAdd } from '@/lib/bulk-add/bulk-session';
import { useEffect } from 'react';

/** Bulk-add page: routes between entry / input / loading / review by state. */
export function BulkAddPage() {
  const bulk = useBulkAdd();

  // On (re)entering the page, if the open session is only analyzing/ready, drop
  // to the entry cards — so coming back while a job runs shows the card list (the
  // analysis keeps going in the background) and a new job can be started, rather
  // than pinning the user to the loading screen. Runs on mount only; starting a
  // job doesn't remount the page, so the happy path still lands on loading.
  const { closePending } = bulk;
  useEffect(() => {
    closePending();
  }, [closePending]);

  const open = bulk.openId ? bulk.sessions.find((s) => s.id === bulk.openId) : undefined;

  if (open) {
    // A draft resumed from the entry cards before "Analyser" was ever clicked.
    if (open.status === 'input') {
      return <BulkInput session={open} />;
    }
    // A session is still on the loading screen until the user begins its review.
    if (open.status === 'analyzing' || open.status === 'ready') {
      return <BulkLoading session={open} />;
    }
    return <BulkReview session={open} />;
  }

  return bulk.screen === 'input' ? <BulkInput /> : <BulkEntry />;
}
